import React, { useState, useEffect } from 'react';
import { getEvents, createEvent, updateEvent, deleteEvent, toggleEventStatus } from '../../services/api';
import { getCategories } from '../../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import EventForm from '../forms/EventForm';
import toast from 'react-hot-toast';

const EventsModule = () => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const columns = ['Image', 'Title', 'Category', 'Date & Time', 'Location', 'Status', 'Actions'];

  useEffect(() => {
    fetchEvents();
    fetchCategories();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await getEvents();
      const eventsData = response.data || response || [];
      setEvents(eventsData);
      console.log('Events loaded:', eventsData.length);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Error fetching events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      let categoriesArray = [];
      if (response && response.data && Array.isArray(response.data)) {
        categoriesArray = response.data.filter(cat => cat.type === 'event');
      } else if (response && Array.isArray(response)) {
        categoriesArray = response.filter(cat => cat.type === 'event');
      }
      setCategories(categoriesArray);
      console.log('Event categories loaded:', categoriesArray.length);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await deleteEvent(id);
        toast.success('Event deleted successfully');
        fetchEvents();
      } catch (error) {
        toast.error('Error deleting event');
      }
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await toggleEventStatus(id);
      toast.success(`Event ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchEvents();
    } catch (error) {
      toast.error('Error toggling event status');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const formatDateTime = (date, time) => {
    if (!date) return 'TBD';
    const dateStr = new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    if (time) {
      return `${dateStr} at ${time}`;
    }
    return dateStr;
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const getCategoryIcon = (categoryName) => {
    const icons = {
      'Conference': '🎤',
      'Webinar': '💻',
      'Workshop': '🔧',
      'Bootcamp': '🏕️',
      'Seminar': '📚',
      'Networking': '🤝'
    };
    return icons[categoryName] || '📅';
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ color: '#0A192F', fontSize: '1.3rem' }}>Events ({events.length})</h2>
          <p style={{ color: '#5A6E7F', fontSize: '0.875rem', marginTop: '0.25rem' }}>
            <i className="fas fa-tags"></i> Categories: {categories.length} event types available
          </p>
        </div>
        <button 
          onClick={() => { setEditingItem(null); setShowModal(true); }} 
          style={{ padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <i className="fas fa-plus" /> Add New Event
        </button>
      </div>

      {events.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', background: '#F7F9FC', borderRadius: '12px' }}>
          <i className="fas fa-calendar-alt" style={{ fontSize: '3rem', color: '#CBD5E1', marginBottom: '1rem' }} />
          <p style={{ color: '#5A6E7F' }}>No events found. Click "Add New Event" to create one.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx} style={{ padding: '1rem', textAlign: 'left' }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '1rem' }}>
                    {event.imageUrl ? (
                      <img src={getFullImageUrl(event.imageUrl)} alt={event.title} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '8px' }} />
                    ) : (
                      <div style={{ width: '40px', height: '40px', background: '#F7F9FC', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="fas fa-calendar" style={{ color: '#CBD5E1' }} />
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>{event.title}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ 
                      background: '#E3F2FD', 
                      padding: '0.2rem 0.6rem', 
                      borderRadius: '20px', 
                      fontSize: '0.75rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem'
                    }}>
                      {getCategoryIcon(event.type)} {event.type}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div><strong>{formatDateTime(event.startDate, event.startTime)}</strong></div>
                    {event.endDate && (
                      <div style={{ fontSize: '0.7rem', color: '#94A3B8' }}>
                        to {formatDateTime(event.endDate, event.endTime)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {event.location}
                    {event.isVirtual && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: '#2C7A7B' }}>(Virtual)</span>}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleToggle(event._id, event.isActive)} 
                      style={{ padding: '0.25rem 0.75rem', borderRadius: '20px', border: 'none', background: event.isActive ? '#D4EDDA' : '#F8D7DA', color: event.isActive ? '#155724' : '#721C24', cursor: 'pointer', fontSize: '0.75rem' }}
                    >
                      {event.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleEdit(event)} 
                      style={{ padding: '0.25rem 0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem' }}
                    >
                      <i className="fas fa-edit" /> Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(event._id)} 
                      style={{ padding: '0.25rem 0.75rem', background: '#DC2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      <i className="fas fa-trash" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <EventForm 
          editingItem={editingItem} 
          categories={categories}
          onClose={handleCloseModal} 
          onRefresh={fetchEvents} 
        />
      )}
    </div>
  );
};

export default EventsModule;